import { useEffect, useMemo } from "react";
import { X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useGetPurchaseOrderQuery,
  useAcceptPurchaseOrderMutation,
  useDeclinePurchaseOrderMutation,
  useGetPurchaseOrderPDFQuery,
} from "@/redux/services/purchaseOrder/purchaseOrderApi";
import { toast } from "sonner";

interface MobilePurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderId: string | null;
  onAcceptOrder?: () => void;
  onRejectOrder?: () => void;
  onStatusChange?: () => void;
}

export default function MobilePurchaseOrderModal({
  isOpen,
  onClose,
  purchaseOrderId,
  onAcceptOrder,
  onRejectOrder,
  onStatusChange,
}: MobilePurchaseOrderModalProps) {
  const {
    data: orderData,
    isLoading: isLoadingOrder,
    refetch: refetchOrder,
    error: orderError,
  } = useGetPurchaseOrderQuery(purchaseOrderId!, {
    skip: !purchaseOrderId || !isOpen,
  });

  const {
    data: pdfData,
    isLoading: isLoadingPdf,
    error: pdfError,
  } = useGetPurchaseOrderPDFQuery(purchaseOrderId!, {
    skip: !purchaseOrderId || !isOpen,
  });

  const [acceptOrder, { isLoading: isAccepting }] =
    useAcceptPurchaseOrderMutation();
  const [declineOrder, { isLoading: isDeclining }] =
    useDeclinePurchaseOrderMutation();

  const order = orderData?.data;
  const currentStatus = order?.accepted_by_vendor;
  const isPending = currentStatus === null || currentStatus === undefined;
  const isLoading = isLoadingOrder || isLoadingPdf;
  const error = orderError || pdfError;

  const getDirectPdfUrl = (): string | null => {
    const documentPath = order?.document_path;
    if (!documentPath) return null;

    const awsUrl = import.meta.env.VITE_AWS_URL || "";
    if (awsUrl) {
      const normalizedPath = documentPath.startsWith("/")
        ? documentPath.slice(1)
        : documentPath;
      const baseUrl = awsUrl.endsWith("/") ? awsUrl : `${awsUrl}/`;
      return `${baseUrl}${normalizedPath}`;
    }

    const apiBase =
      import.meta.env.VITE_PUBLIC_BACKEND_URL || "http://localhost:8000/api";
    const fileBase = apiBase.replace(/\/api\/?$/, "");
    return `${fileBase}${documentPath}`;
  };

  const pdfUrl = getDirectPdfUrl();

  const blobUrl = useMemo(() => {
    if (!pdfData) return null;
    return URL.createObjectURL(
      new Blob([pdfData], { type: "application/pdf" })
    );
  }, [pdfData]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `PO-${order?.document_number || purchaseOrderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (blobUrl) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `PO-${order?.document_number || purchaseOrderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error("PDF not available for download");
    }
  };

  const handleAccept = async () => {
    if (!purchaseOrderId) return;

    try {
      const result = await acceptOrder(purchaseOrderId).unwrap();
      if (result?.success !== false) {
        toast.success(
          result?.message || "Purchase order accepted successfully"
        );
        refetchOrder();
        onStatusChange?.();
        onAcceptOrder?.();
      } else {
        toast.error(result?.message || "Failed to accept purchase order");
      }
    } catch (error: any) {
      console.error("Accept order error:", error);
      toast.error(error?.data?.message || "Failed to accept purchase order");
    }
  };

  const handleReject = async () => {
    if (!purchaseOrderId) return;

    try {
      const result = await declineOrder(purchaseOrderId).unwrap();
      if (result?.success !== false) {
        toast.success(
          result?.message || "Purchase order declined successfully"
        );
        refetchOrder();
        onStatusChange?.();
        onRejectOrder?.();
      } else {
        toast.error(result?.message || "Failed to decline purchase order");
      }
    } catch (error: any) {
      console.error("Decline order error:", error);
      toast.error(error?.data?.message || "Failed to decline purchase order");
    }
  };

  if (!isOpen || !purchaseOrderId) return null;

  const poNumber = order?.document_number || purchaseOrderId || "N/A";

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[95vh] min-h-[58vh] flex flex-col overflow-hidden shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-base font-semibold text-gray-900">
            Purchase Order PDF
          </h2>
          <div className="flex items-center gap-2">
            {(pdfUrl || blobUrl) && (
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                size="sm"
                className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="text-xs font-medium">Download PDF</span>
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto"></div>
                <p className="text-sm text-gray-600">
                  Loading purchase order...
                </p>
              </div>
            </div>
          )}

          {!isLoading && error && !pdfUrl && !blobUrl && (
            <div className="flex items-center justify-center h-full px-6 text-center space-y-2">
              <div>
                <p className="text-sm font-medium text-red-600">
                  Unable to load purchase order
                </p>
                <p className="text-xs text-gray-500">
                  Please try again later or download the document from the
                  desktop view.
                </p>
              </div>
            </div>
          )}

          {!isLoading && (pdfUrl || blobUrl) && (
            <iframe
              src={pdfUrl || blobUrl || undefined}
              className="w-full h-full min-h-[40vh] border-0"
              title={`Purchase Order ${poNumber}`}
            />
          )}
        </div>

        {isPending && !isLoading && (
          <div className="flex flex-col gap-2 px-4 py-3 border-t bg-white">
            <Button
              onClick={handleReject}
              disabled={isAccepting || isDeclining}
              variant="outline"
              className="w-full text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700 py-3 font-semibold"
            >
              {isDeclining ? "Rejecting..." : "Reject Order"}
            </Button>
            <Button
              onClick={handleAccept}
              disabled={isAccepting || isDeclining}
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-3 font-semibold"
            >
              {isAccepting ? "Accepting..." : "Accept Order"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
